/**
 * @fileOverview A view for displaying a list of data with search and filters.
 * It's a dumb component, has no logic and just renders the UI.
 */

import React, { Fragment } from 'react'
import classnames from 'classnames'

import SearchAndFilters from './SearchAndFilters'
import './types'
import Pagination from './Pagination'
import injectStyles from 'react-jss'

const isDefined = val => val !== undefined && val !== null

const styles = {
  wrapperTable: {
    height: 'calc(100vh - 40px)',
    overflowY: 'scroll',
  },
  filtersContainer: {
    content: props => props.path,
    marginRight: 65,
    marginTop: 30,
  },
  pagination: {
    marginLeft: 65,
    fontSize: '80%',
  },
  loadingArea: {
    height: 1050,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}

/**
 * Display a table of data. This does nothing with logic, it's purely
 * presentational
 *
 * @typedef TableTableProps
 * @property {Array<Filter>} filters
 * @property {string} searchText If providing a search filter this is the
 * current text to display in the search box.
 * @property {Function} onSearchTextChange Fired when the search box text
 * is changed. The first argument will be the new search text.
 * @property {Array<SortableHeader>} header The column headers (see types).
 * @property {Array<Array<string>>} data Multi dimensional array to equate to
 * the table rows of data to display
 * @property {number} totalRecords The total amount of records, required to
 * to render the pagination UI.
 * @property {number} offsetRecordsStart The total amount of records per page,
 * required to render the pagination UI.
 * @property {Function} onOffsetChange Fired when a page is changed, the first
 * argument is the new offset.
 * @property {boolean} loadingData When true will show a loading UI.
 * @property {Array<Function>} actions If specified will fire the related
 * action when the row is clicked/tapped.
 * @property {boolean} enableFilterSuggestions Passed through to the
 * SearchAndFilters compontnt, allows you to type in the search box to select
 * filter options via auto complete
 *
 * @param {TableTableProps} props
 *
 * Example usage.
 * <FilterAndPaginationComponent
 *  // Provide the filter options
 *  filters={[
 *    {
 *      // The label that's shown next to the client, could also be a React
 *      // component
 *      label: 'Client',
 *    values: ['Woolworths', 'Uber'],
 *      // Passing in null is akin to selecting 'All'. All will be added as the
 *      // first option.
 *      selectedValue: null,
 *      // Do something with the new selected value
 *      onChange: (newSelectedValue) => { },
 *    },
 *  ]}
 *  // Headers render the table header with any sort direction indication.
 *  header={[
 *    {
 *      // The header title
 *      label: 'Client',
 *      // Show no sort direction
 *      sortDirection: SORT_DIRECTION.NO_SORT,
 *    },
 *    {
 *      // Could also be a UI component, bare in mind, based on the sort option
 *      // a sort arrow will be rendered too
 *      label: <span>Week</span>',
 *      // Show an ascending sort directiom
 *      sortDirection: SORT_DIRECTION.ASCENDING,
 *      // Use this to toggle the sort direction, or add your own behaviour
 *      onSortDirectionToggle: () => { },
 *    },
 *  ]}
 *  // This is the tabular data to display, it's a multi dimensional array.
 *  data={[
 *    ['Woolworths', 'W34'],
 *    ['Woolworths', 'W35'],
 *    // Can pass anything in that's rendered.
 *    ['Uber', <span style={{fontWeight: 'bold'}}>W34</span>]
 *  ]}
 *  // Used for paging
 *  totalRecords={40093}
 *  // In this example we've showing skipped 40 records and are showing records
 *  // starting at 41
 *  offsetRecordsStart={40}
 *  // If the user selected page 5, this would fire and the first argument would
 *  // be 80 because we show 20 records per page, so page 4 would start from
 *  // record 81 (offset 80)
 *  onOffsetChange={newOffset => {}}
 *  // In this example we're showing 20 records per page.
 *  resultsPerPage={20}
 *  // Indicate the table is loading data.
 *  loadingData={true}
 *  // The current search text
 *  searchText={'test'}
 *  // When the search text is changed and a new search is triggered this will
 *  // fire. The first argument will be the new search string.
 *  onSearchTextChange={searchText => {}}
 *  // This is responsible for rendering the list of items.
 *  Component={TableTable}
 * />
 */
const FilterAndPaginationComponent = props => {
  const {
    filters,
    searchText,
    onSearchTextChange,
    data,
    totalRecords,
    offsetRecordsStart,
    onOffsetChange,
    resultsPerPage,
    setResultsPerPage,
    hasEditableAmountItemsPerPage,
    classes,
    loadingData,
    actions,
    footer,
    showPages = true,
    enableFilterSuggestions,
    Component,
    presets,
    hasVerticalScroll,
    ...otherProps
  } = props

  return (
    <div>
      {(filters && filters.length) || onSearchTextChange || searchText ? (
        <SearchAndFilters
          presets={presets}
          enableFilterSuggestions={enableFilterSuggestions}
          filters={filters}
          searchText={searchText}
          onSearchTextChange={onSearchTextChange}
          className={classes.filtersContainer}
        />
      ) : null}
      {loadingData ? (
        <div className={classes.loadingArea}>Loading...</div>
      ) : (
        <Fragment>
          <div
            className={classnames({
              [classes.wrapperTable]: hasVerticalScroll,
            })}
          >
            <Component
              data={data}
              actions={actions}
              footer={footer}
              {...otherProps}
            />
          </div>
          {showPages ? (
            <Pagination
              total={isDefined(totalRecords) ? totalRecords : data.length}
              offset={isDefined(offsetRecordsStart) ? offsetRecordsStart : 0}
              resultsPerPage={
                isDefined(resultsPerPage) ? resultsPerPage : data.length
              }
              setResultsPerPage={setResultsPerPage}
              onOffsetChange={onOffsetChange}
              className={classes.pagination}
              hasEditableAmountItemsPerPage={hasEditableAmountItemsPerPage}
            />
          ) : null}
        </Fragment>
      )}
      {/* Clears collapsing margins on the pagination. */}
      <div style={{ display: 'inline-block' }} />
    </div>
  )
}

const StyledFilterAndPaginationComponent = injectStyles(styles)(
  FilterAndPaginationComponent,
)

export default StyledFilterAndPaginationComponent



// WEBPACK FOOTER //
// ./src/Components/FilterAndPaginationComponent/index.js